// src/utils/SceneManager.js
// v7 - Removed final delay in _waitForSceneActive

import { auth } from '../firebaseConfig.js';
import CanvasFixer from '../canvasFixer.js';
import Phaser from 'phaser';
import { hasSpecialAccess } from '../services/specialAccessService.js';
import { foucPrevention } from '../services/FOUCPreventionSystem.js';


class SceneManager {
    constructor() {
        this.currentSceneKey = null;
        this.previousSceneKey = null;
        this.isTransitioning = false;
        this.transitionCooldown = false; // New flag
        this.transitionCooldownDuration = 150; // 1 second cooldown
        this.gameInstance = null;
        this.eventBus = null;
        this.transitionQueue = [];
        this._transitionStartTime = null;
        this._transitionTimeoutId = null;
        this._targetSceneKey = null;

        this.TRANSITION_TIMEOUT_DURATION = 10000;
        this.FADE_DURATION = 350; // Professional smooth fade duration
        this.POST_STOP_DELAY = 10;

        this.loadingElementsContainer = null;
        this.isLoadingOverlayVisible = false;
        this.loadingOverlayContext = null;
    }

    _forceCleanupStuckElements() {
        console.log("🔍 [SceneManager] ADVANCED DEBUG - Force cleanup of stuck loading elements");
        
        // COMPREHENSIVE VISUAL DEBUG - Show everything on screen
        this._debugVisibleElements();
        
        // Remove any DOM elements that might be stuck
        const stuckElements = document.querySelectorAll('[class*="loading"]:not(#preloader):not(#preloader *)');
        console.log("🔍 Found stuck DOM loading elements:", stuckElements.length);
        stuckElements.forEach((element, index) => {
            console.log(`🔍 Stuck DOM element ${index}:`, {
                id: element.id,
                className: element.className,
                visible: getComputedStyle(element).display !== 'none',
                position: getComputedStyle(element).position,
                zIndex: getComputedStyle(element).zIndex
            });
            
            // Fix specific stuck elements before removing
            if (element.className === 'signin-loading') {
                element.style.display = 'none';
                console.log('🔧 Fixed stuck signin-loading element');
            }
            
            element.remove();
        });
        
        // Clean up Phaser loading elements across all scenes
        if (this.gameInstance && this.gameInstance.scene) {
            console.log("🔍 Checking", this.gameInstance.scene.scenes.length, "Phaser scenes for stuck containers");
            
            this.gameInstance.scene.scenes.forEach((scene, index) => {
                if (scene && scene.children && scene.children.list) {
                    const allContainers = scene.children.list.filter(child => child && child.type === 'Container');
                    const stuckContainers = allContainers.filter(child => child.depth >= 99999);
                    
                    console.log(`🔍 Scene ${scene.scene.key}: ${allContainers.length} containers total, ${stuckContainers.length} high-depth`);
                    
                    stuckContainers.forEach((container, containerIndex) => {
                        try {
                            console.log(`🔍 Removing stuck container ${containerIndex} from ${scene.scene.key}:`, {
                                depth: container.depth,
                                visible: container.visible,
                                active: container.active,
                                children: container.list?.length || 0
                            });
                            container.destroy();
                        } catch (e) {
                            console.warn("🔍 Error force removing container:", e);
                        }
                    });
                }
            });
        }
        
        // FINAL VISUAL DEBUG - Show what's left
        console.log("🔍 [SceneManager] AFTER cleanup - Final visual state:");
        this._debugVisibleElements();
    }

    _debugVisibleElements() {
        console.log("🔍 ====== VISUAL DEBUG: EVERYTHING ON SCREEN ======");
        
        // Check HTML preloader
        const preloader = document.getElementById('preloader');
        if (preloader) {
            const preloaderStyle = getComputedStyle(preloader);
            console.log("🔍 HTML Preloader:", {
                display: preloaderStyle.display,
                visibility: preloaderStyle.visibility,
                opacity: preloaderStyle.opacity,
                zIndex: preloaderStyle.zIndex,
                position: preloaderStyle.position
            });
        }
        
        // Check game container
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            const gameStyle = getComputedStyle(gameContainer);
            console.log("🔍 Game Container:", {
                display: gameStyle.display,
                visibility: gameStyle.visibility,
                opacity: gameStyle.opacity,
                children: gameContainer.children.length
            });
        }
        
        // Check for any visible loading-related elements
        const loadingElements = document.querySelectorAll('[id*="loading"], [class*="loading"], [id*="loader"], [class*="loader"]');
        console.log("🔍 All loading-related DOM elements:", loadingElements.length);
        loadingElements.forEach((element, index) => {
            const style = getComputedStyle(element);
            console.log(`🔍 Loading element ${index}:`, {
                id: element.id,
                className: element.className,
                display: style.display,
                visibility: style.visibility,
                opacity: style.opacity,
                zIndex: style.zIndex,
                position: style.position
            });
        });
        
        // Check all DOM overlays
        const overlays = document.querySelectorAll('[id*="overlay"], [class*="overlay"]');
        console.log("🔍 All overlay DOM elements:", overlays.length);
        overlays.forEach((element, index) => {
            const style = getComputedStyle(element);
            if (style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) > 0) {
                console.log(`🔍 VISIBLE overlay ${index}:`, {
                    id: element.id,
                    className: element.className,
                    zIndex: style.zIndex
                });
            }
        });
        
        console.log("🔍 ===============================================");
    }

    init(game) {
        this.gameInstance = game;
        this.eventBus = game.events;
        console.log("SceneManager initialized");

        this.gameInstance.events.on('shutdown', (sys) => {
            if (this.isTransitioning && sys.scene?.scene?.key === this.currentSceneKey) {
                console.warn(`SceneManager: Resetting transition flag during unexpected shutdown of ${this.currentSceneKey}.`);
                this._clearTransitionState("shutdown_interrupt");
            } else if (this.isTransitioning && this._targetSceneKey === sys.scene?.scene?.key) {
                console.warn(`SceneManager: Target scene ${this._targetSceneKey} shut down unexpectedly during transition.`);
                this._clearTransitionState("target_shutdown_interrupt");
                this._handleTransitionError(new Error(`Target scene ${this._targetSceneKey} shut down`), this._targetSceneKey);
            }
            if (this.loadingOverlayContext && this.loadingOverlayContext.scene === sys.scene) {
                console.warn(`[SceneManager] Overlay context scene (${sys.scene?.scene?.key}) shut down. Destroying overlay.`);
                this._destroyLoadingOverlay();
                this.loadingOverlayContext = null;
            }
        });

        window.sceneTransitionInProgress = false;
        window.sceneTransitionStartTime = null;
        window.sceneTransitionClearTimeout = null;

        return this;
    }

    getActiveSceneInstance() {
        if (!this.gameInstance || !this.gameInstance.scene) {
            return null;
        }
        try {
            const activeScenes = this.gameInstance.scene.getScenes(true);
            if (activeScenes.length > 0) {
                if (this.currentSceneKey && activeScenes.some(s => s.scene.key === this.currentSceneKey)) {
                    return activeScenes.find(s => s.scene.key === this.currentSceneKey);
                }
                return activeScenes[0];
            }
            if (this.currentSceneKey) {
                const scene = this.gameInstance.scene.getScene(this.currentSceneKey);
                if (scene && this.gameInstance.scene.isActive(this.currentSceneKey)) {
                    return scene;
                }
            }
            return null;
        } catch (error) {
            console.error("Error in getActiveSceneInstance:", error);
            return null;
        }
    }

    _showLoadingOverlay(targetSceneKey) {
        const currentScene = this.getActiveSceneInstance();
        if (!currentScene || !currentScene.add || !currentScene.cameras?.main) {
            console.error("[SceneManager] Cannot show loading overlay: No valid current scene context or camera.");
            return;
        }
        
        // Skip loading overlay for DOM scene transitions - they have their own loading
        const isDOMTransition = (currentScene.scene.key.includes('DOM') || currentScene.scene.key.includes('Universal') || currentScene.scene.key.includes('Desktop')) ||
                               (targetSceneKey.includes('DOM') || targetSceneKey.includes('Universal') || targetSceneKey.includes('Desktop'));
        
        if (isDOMTransition) {
            console.log("[SceneManager] Skipping loading overlay for DOM scene transition");
            this.isLoadingOverlayVisible = false;
            return;
        }
        
        this.loadingOverlayContext = currentScene;

        this._destroyLoadingOverlay();

        console.debug("[SceneManager] Creating modern loading overlay using context:", currentScene.scene.key);
        this.isLoadingOverlayVisible = true;

        const camera = currentScene.cameras.main;
        const width = camera.width;
        const height = camera.height;
        const centerX = width / 2;
        const centerY = height / 2;

        this.loadingElementsContainer = currentScene.add.container(0, 0);
        this.loadingElementsContainer.setDepth(99999);
        this.loadingElementsContainer.setScrollFactor(0);

        // Modern dark background matching DOM design
        const bg = currentScene.add.rectangle(centerX, centerY, width, height, 0x0f172a, 0.95).setOrigin(0.5);
        this.loadingElementsContainer.add(bg);
        
        // Professional loading text matching DOM design
        const text = currentScene.add.text(centerX, centerY - 30, `Transitioning...`, {
            fontFamily: 'Segoe UI, sans-serif', fontSize: '18px', color: '#FFFFFF', align: 'center', fontWeight: '600'
        }).setOrigin(0.5);
        this.loadingElementsContainer.add(text);
        
        // Modern progress bar matching DOM design  
        const barBg = currentScene.add.rectangle(centerX, centerY + 20, 300, 8, 0x1e293b, 0.8).setOrigin(0.5);
        this.loadingElementsContainer.add(barBg);
        const bar = currentScene.add.rectangle(centerX - 150, centerY + 20, 150, 8, 0x00d4ff, 0.9).setOrigin(0, 0.5);
        this.loadingElementsContainer.add(bar);
        
        // Add animated glow effect
        currentScene.tweens.add({
            targets: bar,
            alpha: { from: 0.9, to: 1 },
            duration: 1000,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });

        this.loadingElementsContainer.setAlpha(0);
        this.loadingElementsContainer.setVisible(true);

        currentScene.tweens.add({
            targets: this.loadingElementsContainer,
            alpha: 1,
            duration: 200,
            ease: 'Sine.easeOut' // Professional smooth fade-in
        });

        currentScene.tweens.add({
            targets: bar,
            width: 300,
            duration: 800,
            ease: 'Sine.easeInOut',
            yoyo: true,
            repeat: -1
        });
    }

    _hideLoadingOverlay() {
        if (!this.loadingElementsContainer || !this.isLoadingOverlayVisible) {
            return;
        }
        console.debug("[SceneManager] Hiding and destroying loading overlay.");
        this.isLoadingOverlayVisible = false;

        const containerToDestroy = this.loadingElementsContainer;
        const contextScene = this.loadingOverlayContext;

        this.loadingElementsContainer = null;
        this.loadingOverlayContext = null;

        if (contextScene && contextScene.scene?.isActive(contextScene.scene.key) && contextScene.tweens) {
            contextScene.tweens.add({
                targets: containerToDestroy,
                alpha: 0,
                duration: 150,
                ease: 'Linear',
                onComplete: () => {
                    this._destroyContainer(containerToDestroy, contextScene);
                }
            });
        } else {
            console.warn("[SceneManager] Overlay context invalid during hide. Destroying immediately.");
            this._destroyContainer(containerToDestroy, contextScene);
        }
    }

    _destroyLoadingOverlay() {
        if (this.loadingElementsContainer) {
            console.log("[SceneManager] Force destroying loading overlay elements.");
            const containerToDestroy = this.loadingElementsContainer;
            const contextScene = this.loadingOverlayContext;
            this.loadingElementsContainer = null;
            this.loadingOverlayContext = null;
            this.isLoadingOverlayVisible = false;
            this._destroyContainer(containerToDestroy, contextScene);
        }
    }

    _destroyContainer(container, contextScene = null) {
        if (container) {
            try {
                const tweenManager = (contextScene && contextScene.scene?.isActive(contextScene.scene.key) && contextScene.tweens)
                                     ? contextScene.tweens
                                     : this.gameInstance?.tweens;

                tweenManager?.killTweensOf(container);
                if (container.list) {
                    tweenManager?.killTweensOf(container.getAll());
                }
                container.destroy(true);
            } catch (e) {
                if (!e.message?.toLowerCase().includes('destroyed')) {
                    console.warn("[SceneManager] Error destroying loading overlay container:", e);
                }
            }
        }
    }

    async switchToScene(targetSceneKey, data = {}) {
        const transitionId = `Transition_${Date.now()}`;
        console.time(transitionId);
        console.log(`%c[SceneManager ${transitionId}] >>>>> Request transition >>>>>`, 'background: #222; color: #bada55');

        const currentSceneInstance = this.getActiveSceneInstance();
        const currentKey = currentSceneInstance?.scene?.key || this.currentSceneKey || 'N/A';

        // --- ADD COOLDOWN CHECK ---
        if (this.transitionCooldown) {
            console.warn(`[SceneManager ${transitionId}] Transition request ignored (in cooldown). Target: ${targetSceneKey}`);
            console.timeEnd(transitionId);
            return;
        }
        // --- END COOLDOWN CHECK ---


        if (targetSceneKey === currentKey && !this.isTransitioning) {
             console.warn(`[SceneManager ${transitionId}] Transition request ignored (already in target scene ${targetSceneKey}).`);
             if (window.UIController && !window.UIController.isAnyOverlayVisible()) {
                  window.UIController.enableSceneInput();
             }
             console.timeEnd(transitionId);
             return;
        }

        console.log(`%c[SceneManager ${transitionId}] Request: ${currentKey} -> ${targetSceneKey}. Current state: ${this.isTransitioning ? 'TRANSITIONING' : 'IDLE'}`, 'color: #00AAFF');

        if (this.isTransitioning) {
            const alreadyQueued = this.transitionQueue.some(t => t.targetSceneKey === targetSceneKey);
            const alreadyTargeting = this._targetSceneKey === targetSceneKey;
            if (alreadyQueued || alreadyTargeting) {
                 console.warn(`[SceneManager ${transitionId}] Transition to ${targetSceneKey} already in progress or queued. Ignoring duplicate request.`);
                 console.timeEnd(transitionId);
                 return;
            }
            console.log(`[SceneManager ${transitionId}] Queuing transition to ${targetSceneKey} (current target: ${this._targetSceneKey || 'N/A'}).`);
            this.transitionQueue.push({ targetSceneKey, data });
            console.timeEnd(transitionId);
            return;
        }

        this._targetSceneKey = targetSceneKey;
        this._setTransitionState(true);
        console.log(`%c[SceneManager ${transitionId}] Starting transition: ${currentKey} -> ${targetSceneKey}`, 'color: #00d4ff; font-weight: bold');

        try {
            console.time(`${transitionId}_Overall`);

            // FOUC Prevention: Set consistent background immediately
            console.time(`${transitionId}_FOUCPrevention`);
            foucPrevention.setTransitionBackground('#0f172a');
            console.timeEnd(`${transitionId}_FOUCPrevention`);

            console.time(`${transitionId}_TimeoutSetup`);
            this._startTransitionTimeout(currentKey, targetSceneKey);
            console.timeEnd(`${transitionId}_TimeoutSetup`);

            console.time(`${transitionId}_AuthCheck`);
            const authResult = this._checkAuth(targetSceneKey);
            if (!authResult.allowed) {
                throw new Error(`AUTH_REDIRECT_${authResult.redirectScene}`);
            }
            console.timeEnd(`${transitionId}_AuthCheck`);

            console.time(`${transitionId}_DisableInput`);
            if (window.UIController) window.UIController.disableSceneInput();
            
            // CRITICAL: Clean up any authentication dialogs during transitions
            this.cleanupAuthenticationDialogs();
            
            console.timeEnd(`${transitionId}_DisableInput`);

            console.time(`${transitionId}_FadeOut`);
            if (currentSceneInstance && currentKey !== 'BootSceneDOM' && currentSceneInstance.cameras?.main) {
                await this._cameraFade(currentSceneInstance.cameras.main, 'out');
            } else {
                await new Promise(resolve => setTimeout(resolve, 10));
            }
            console.timeEnd(`${transitionId}_FadeOut`);

            console.time(`${transitionId}_ShowLoading`);
            // Skip loading overlay for DOM scene transitions - they handle their own loading
            const isDOMTransition = (currentKey && (currentKey.includes('DOM') || currentKey.includes('Universal') || currentKey.includes('Desktop'))) ||
                                   (targetSceneKey.includes('DOM') || targetSceneKey.includes('Universal') || targetSceneKey.includes('Desktop'));
            
            if (!isDOMTransition) {
                this._showLoadingOverlay(targetSceneKey);
            } else {
                console.log(`[SceneManager] Skipping loading overlay for DOM transition: ${currentKey} -> ${targetSceneKey}`);
            }
            console.timeEnd(`${transitionId}_ShowLoading`);

            console.time(`${transitionId}_StopScene`);
            if (currentKey && currentKey !== 'N/A' && this.gameInstance.scene.isActive(currentKey)) {
                console.log(`[SceneManager ${transitionId}] Stopping scene ${currentKey}...`);
                try {
                    this.gameInstance.scene.stop(currentKey);
                    await new Promise(resolve => setTimeout(resolve, this.POST_STOP_DELAY));
                    console.log(`[SceneManager ${transitionId}] Scene ${currentKey} stopped.`);
                } catch (e) { console.error(`[SceneManager ${transitionId}] Error stopping scene ${currentKey}:`, e); }
            } else {
                 console.log(`[SceneManager ${transitionId}] No active previous scene (${currentKey}) found to stop.`);
            }
            console.timeEnd(`${transitionId}_StopScene`);

            console.time(`${transitionId}_UpdateState`);
            this.previousSceneKey = currentKey;
            this.currentSceneKey = targetSceneKey;
            console.timeEnd(`${transitionId}_UpdateState`);

            console.time(`${transitionId}_StartSceneCall`);
            console.log(`[SceneManager ${transitionId}] Calling scene.start('${targetSceneKey}')...`);
            if (!this.gameInstance.scene.keys[targetSceneKey]) {
                throw new Error(`Scene key "${targetSceneKey}" does not exist.`);
            }
            this.gameInstance.scene.start(targetSceneKey, data);
            console.timeEnd(`${transitionId}_StartSceneCall`);

            console.time(`${transitionId}_WaitForActive`);
            console.log(`[SceneManager ${transitionId}] Waiting for ${targetSceneKey} to become active...`);
            const newSceneInstance = await this._waitForSceneActive(targetSceneKey);
            console.log(`[SceneManager ${transitionId}] Scene ${targetSceneKey} is active.`);
            console.timeEnd(`${transitionId}_WaitForActive`);

            console.time(`${transitionId}_HideLoading`);
            // Force cleanup of any stuck loading elements
            this._hideLoadingOverlay();
            this._forceCleanupStuckElements();
            console.timeEnd(`${transitionId}_HideLoading`);

            console.time(`${transitionId}_PostStartSetup`);
            if (window.UIController) window.UIController.setActiveScene(newSceneInstance);
            if (typeof CanvasFixer !== 'undefined' && CanvasFixer.fixSceneCanvas) {
                 CanvasFixer.fixSceneCanvas(newSceneInstance);
            }
            console.timeEnd(`${transitionId}_PostStartSetup`);

            console.time(`${transitionId}_FadeIn`);
            if (newSceneInstance && newSceneInstance.cameras?.main) {
                await this._cameraFade(newSceneInstance.cameras.main, 'in');
            } else {
                 console.warn(`[SceneManager ${transitionId}] Cannot fade in ${targetSceneKey}, instance or camera missing.`);
            }
            console.timeEnd(`${transitionId}_FadeIn`);

            console.time(`${transitionId}_FinalCleanup`);
            this._clearTransitionTimeout();
            console.log(`%c[SceneManager ${transitionId}] Transition complete: ${targetSceneKey}`, 'color: #00d4ff; font-weight: bold');
            this.eventBus?.emit('transition-end', { scene: targetSceneKey });
            console.timeEnd(`${transitionId}_FinalCleanup`);

            console.timeEnd(`${transitionId}_Overall`);

            console.time(`${transitionId}_ExtraCanvasFix`);
             if (typeof CanvasFixer !== 'undefined' && CanvasFixer.fixCanvas) {
                  CanvasFixer.fixCanvas();
             }
            console.timeEnd(`${transitionId}_ExtraCanvasFix`);

        } catch (error) {
            console.error(`%c[SceneManager ${transitionId}] Transition FAILED (${currentKey} -> ${targetSceneKey}): ${error.message}`, 'color: #FF0000; font-weight: bold', error);
            this._clearTransitionTimeout();
            this._destroyLoadingOverlay();
            this._handleTransitionError(error, targetSceneKey);
            this.eventBus?.emit('transition-error', { target: targetSceneKey, error });
            console.timeEnd(`${transitionId}_Overall`);

        } finally {
            this._setTransitionState(false); // Reset flag FIRST

              // --- START COOLDOWN ---
              this.transitionCooldown = true;
              setTimeout(() => { 
                this.transitionCooldown = false;
                console.log(`[SceneManager] Transition cooldown cleared after ${this.transitionCooldownDuration}ms`);
              }, this.transitionCooldownDuration);
              console.log(`[SceneManager] Transition cooldown started for ${this.transitionCooldownDuration}ms`);
              // --- END COOLDOWN ---

            if (window.UIController && !window.UIController.isAnyOverlayVisible()) {
                console.log(`[SceneManager ${transitionId}] Attempting final input enable.`);
                window.UIController.enableSceneInput();
            } else {
                 console.log(`[SceneManager ${transitionId}] Final input enable skipped (overlay visible or UIController missing).`);
            }

            this._processQueue();
            console.log(`%c[SceneManager ${transitionId}] <<<<< Transition finished <<<<<`, 'background: #222; color: #bada55');
            console.timeEnd(transitionId);
        }
    }

    _setTransitionState(isTransitioning) {
        this.isTransitioning = isTransitioning;
        window.sceneTransitionInProgress = isTransitioning;
        if (isTransitioning) {
            this._transitionStartTime = Date.now();
            window.sceneTransitionStartTime = this._transitionStartTime;
        } else {
            this._transitionStartTime = null;
            window.sceneTransitionStartTime = null;
            this._targetSceneKey = null;
        }
    }

    _clearTransitionState(reason = "unknown") {
        console.warn(`[SceneManager] Force clearing transition state. Reason: ${reason}`);
        this._clearTransitionTimeout();
        this._destroyLoadingOverlay();
        this._setTransitionState(false);
        this.transitionQueue = [];
        if (window.UIController) window.UIController.enableSceneInput();
   }

    _startTransitionTimeout(currentKey, targetKey) {
        this._clearTransitionTimeout();
        this._transitionTimeoutId = setTimeout(() => {
            console.error(`%c[SceneManager] TRANSITION TIMEOUT (${this.TRANSITION_TIMEOUT_DURATION}ms) for ${currentKey} -> ${targetKey}. Attempting recovery.`, 'color: #FF0000; font-weight: bold');
            this._transitionTimeoutId = null;
            window.sceneTransitionClearTimeout = null;
            this._handleTransitionError(new Error("Transition Timeout"), targetKey);
        }, this.TRANSITION_TIMEOUT_DURATION);
        window.sceneTransitionClearTimeout = this._transitionTimeoutId;
    }

    _clearTransitionTimeout() {
        if (this._transitionTimeoutId) {
            clearTimeout(this._transitionTimeoutId);
            this._transitionTimeoutId = null;
        }
        if (window.sceneTransitionClearTimeout) {
            clearTimeout(window.sceneTransitionClearTimeout);
            window.sceneTransitionClearTimeout = null;
        }
    }

    // --- REPLACE THIS ENTIRE FUNCTION ---
    // src/utils/SceneManager.js

// --- REPLACE THIS ENTIRE FUNCTION ---
_checkAuth(targetSceneKey) {
    // --- MODIFIED: Removed 'MultiplayerGameOverScene' from authOnlyScenes ---
    const authOnlyScenes = [
        "AuthenticatedMenuScene", "AuthenticatedMenuSceneDOM", 
        "ProfileScene", "ProfileSceneDOM", "MultiplayerMenuScene",
        "CreateGameScene", "JoinGameScene", "LobbyScene",
        "MultiplayerGameScene", "AdminConsoleScene", // Removed MultiplayerGameOverScene
        
    ];
    // CRITICAL FIX: Update guest only scenes to include DOM scenes
    const guestOnlyScenes = ["GuestMenuScene", "GuestMenuSceneDOM"];
    // --- END MODIFICATION ---

    const user = auth.currentUser;
    const specialAccess = typeof hasSpecialAccess === 'function' ? hasSpecialAccess() : false; // Safety check for function existence
    const isVerifiedUser = !!(user && user.emailVerified);
    const isGuestPlus = !user && specialAccess;
    const isEffectivelyLoggedIn = isVerifiedUser || isGuestPlus;

    // --- Detailed Logging ---
    const currentKey = this.currentSceneKey || 'N/A';
    console.log(`[SceneManager CheckAuth] From: ${currentKey} -> Target: ${targetSceneKey}`);
    console.log(`  - User UID: ${user?.uid || 'null'}, Verified: ${isVerifiedUser}`);
    console.log(`  - Special Access: ${specialAccess}, Is Guest+: ${isGuestPlus}`);
    console.log(`  - Is Effectively Logged In: ${isEffectivelyLoggedIn}`);
    // --- End Logging ---

    // --- Rule 1: Trying to access Auth-Only Scene ---
    if (authOnlyScenes.includes(targetSceneKey)) {
        if (!isEffectivelyLoggedIn) {
            console.warn(`[CheckAuth] DENIED to ${targetSceneKey} (Auth Required). Redirecting to DOM scene.`);
            const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
            const redirectScene = useUniversalLanding ? "MainMenuSceneUniversal" : "GuestMenuSceneDOM";
            return { allowed: false, redirectScene: redirectScene };
        }
        console.log(`[CheckAuth] ALLOWED to ${targetSceneKey} (Effectively Logged In).`);
        return { allowed: true }; // Allow verified users AND Guest+
    }

    // --- Rule 2: Trying to access Guest-Only Scene ---
    if (guestOnlyScenes.includes(targetSceneKey)) {
        if (isEffectivelyLoggedIn) {
            // If logged in (verified OR Guest+), redirect to AuthenticatedMenu DOM
            console.warn(`[CheckAuth] DENIED to ${targetSceneKey} (Guest Only). Redirecting to AuthenticatedMenuSceneDOM.`);
            return { allowed: false, redirectScene: "AuthenticatedMenuSceneDOM" };
        }
        // If truly a guest, allow
        console.log(`[CheckAuth] ALLOWED to ${targetSceneKey} (Not Logged In).`);
        return { allowed: true };
    }

    // --- Rule 3: Accessing MainMenuScene, MultiplayerGameOverScene, or other public scenes ---
    // MultiplayerGameOverScene is now treated as public here because it was removed from authOnlyScenes
    console.log(`[CheckAuth] ALLOWED to ${targetSceneKey} (Public Scene or Game Over).`);
    return { allowed: true };
}
// --- END REPLACEMENT FUNCTION ---

    _cameraFade(camera, direction = 'out') {
        return new Promise(resolve => {
            if (!camera || !camera.scene || !camera.scene.tweens) {
                 console.warn(`[SceneManager] Invalid camera or scene tweens for fade ${direction}. Skipping fade.`);
                 resolve();
                 return;
            }
            const alphaValue = direction === 'out' ? 0 : 1;
            const startAlpha = direction === 'out' ? 1 : 0;
            camera.setAlpha(startAlpha);

            camera.scene.tweens.add({
                targets: camera,
                alpha: alphaValue,
                duration: this.FADE_DURATION,
                ease: 'Sine.easeInOut', // Professional smooth easing
                onComplete: () => {
                    if (camera.scene?.scene?.isActive(camera.scene.scene.key)) {
                        resolve();
                    } else {
                        console.warn(`[SceneManager] Fade completed for ${direction}, but scene ${camera.scene?.scene?.key} is no longer active. Not resolving promise.`);
                    }
                },
                onCompleteScope: this
            });
            setTimeout(() => { resolve(); }, this.FADE_DURATION + 100); // Adjusted for smoother timing
        });
    }

    _waitForSceneActive(sceneKey) {
        return new Promise((resolve, reject) => {
            // Determine scene type for optimization
            const isDOMScene = sceneKey.includes('DOM') || sceneKey.includes('Universal') || sceneKey.includes('Template') || sceneKey.includes('Desktop');
            
            let checkAttempts = 0; 
            const maxAttempts = isDOMScene ? 20 : 40; // Reduced for game scenes
            const checkInterval = isDOMScene ? 50 : 100; // Faster for DOM scenes 
            const waitStartTime = Date.now();
            
            const checkScene = () => {
                if (!this.isTransitioning || this._transitionTimeoutId === null) { 
                    const waitDuration = Date.now() - waitStartTime; 
                    console.warn(`[SceneManager] Transition cancelled while waiting for ${sceneKey} activation (after ${waitDuration}ms).`); 
                    reject(new Error("Transition cancelled")); 
                    return; 
                }
                
                checkAttempts++; 
                const targetInstance = this.gameInstance.scene.getScene(sceneKey); 
                const isActive = targetInstance && this.gameInstance.scene.isActive(sceneKey); 
                const isCreated = targetInstance && targetInstance.sys.settings.status === Phaser.Scenes.RUNNING; 
                
                // CRITICAL FIX: DOM scenes, Universal scenes, and Template scenes don't rely on cameras
                const isDOMScene = sceneKey.includes('DOM') || sceneKey.includes('Universal') || sceneKey.includes('Template') || sceneKey.includes('Desktop');
                console.log(`[SceneManager] Scene check: ${sceneKey}, isDOMScene: ${isDOMScene}, Active: ${isActive}, Created: ${isCreated}`);
                
                // Optimized camera readiness check
                const cameraReady = isDOMScene ? true : (
                    targetInstance?.cameras?.main && (
                        (targetInstance.cameras.main.width > 0 && targetInstance.cameras.main.height > 0) ||
                        checkAttempts >= 15 // After 1.5 seconds, assume camera is ready enough
                    )
                );
                
                // DOM scenes are ready much faster - skip extended checks
                if (isDOMScene && isActive && isCreated) {
                    console.log(`[SceneManager] DOM/Universal scene ${sceneKey} ready (fast path)`);
                    resolve(targetInstance);
                    return;
                }
                
                // NEW: Check for UI readiness signal (graceful fallback for scenes without this flag)
                const isUIReady = targetInstance?.isUIReady !== false;
                
                // Additional micro-delay for background rendering consistency
                const hasConsistentBackground = checkAttempts >= 2; // Ensure background has time to render
                
                if (isActive && isCreated && cameraReady && isUIReady && hasConsistentBackground) { 
                    const waitDuration = Date.now() - waitStartTime; 
                    console.log(`[SceneManager] Scene ${sceneKey} active, created, camera ready, UI ready, and background rendered after ${waitDuration}ms and ${checkAttempts} attempts.`); 
                    resolve(targetInstance); 
                } 
                else if (checkAttempts >= maxAttempts) { 
                    const waitDuration = Date.now() - waitStartTime; 
                    console.error(`[SceneManager] Timeout waiting for ${sceneKey} (Active: ${isActive}, Created: ${isCreated}, Camera: ${cameraReady}, UIReady: ${isUIReady}, Background: ${hasConsistentBackground}) after ${waitDuration}ms.`); 
                    reject(new Error(`Timeout waiting for scene ${sceneKey}`)); 
                }
                else { 
                    setTimeout(checkScene, checkInterval); 
                }
            }; 
            checkScene();
        });
    }

    // src/utils/SceneManager.js -> _handleTransitionError() - CORRECTED

   // src/utils/SceneManager.js -> _handleTransitionError() - CORRECTED

  // src/utils/SceneManager.js -> _handleTransitionError() - CORRECTED

  _handleTransitionError(error, failedTargetScene) {
    console.error(`[SceneManager] Handling transition error for target ${failedTargetScene}:`, error);

    // --- KEY FIX: SHOW A USER-FACING MESSAGE ---
    // Use the global DOM-based utility to show an error toast.
    // This is safe because it doesn't depend on the broken Phaser scene.
    if (window.showGeneralMessage) {
        window.showGeneralMessage("Error loading scene. Returning to menu.", 4000, true);
    }
    // --- END KEY FIX ---

    this._destroyLoadingOverlay();
    
    // Determine recovery target based on feature flags
    const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
    const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
    
    // CRITICAL FIX: Always use DOM or Universal scenes for recovery
    let recoveryTarget = "MainMenuSceneUniversal"; // Modern default
    if (useUniversalLanding) {
      recoveryTarget = "MainMenuSceneUniversal";
    } else {
      recoveryTarget = "GuestMenuSceneDOM"; // Always use DOM scenes for recovery
    }
    
    if (error.message?.startsWith('AUTH_REDIRECT_')) { 
      recoveryTarget = error.message.split('AUTH_REDIRECT_')[1] || recoveryTarget; 
    } else if (failedTargetScene === 'BootSceneDOM') { 
      console.error("CRITICAL: BootSceneDOM transition failed."); 
      document.body.innerHTML = `<div style="color: red; padding: 20px;">Critical Error: Game failed to initialize. Please refresh.</div>`; 
      this._setTransitionState(false); 
      return; 
    }
    
    console.log(`[SceneManager] Recovery Target: ${recoveryTarget} (Universal: ${useUniversalLanding}, DOM: ${useDOMMenus})`);
    
    try {
        this._setTransitionState(false); 
        if (this.gameInstance?.cameras?.main) {
            this.gameInstance.cameras.main.resetFX();
            this.gameInstance.cameras.main.setAlpha(1);
        }
        if (window.UIController) window.UIController.hideAllOverlays();
        
        const activeScenes = this.gameInstance.scene.getScenes(true);
        activeScenes.forEach(scene => { if (scene.scene.key !== recoveryTarget) { try { this.gameInstance.scene.stop(scene.scene.key); } catch (e) { console.warn(`[Recovery] Error stopping ${scene.scene.key}:`, e); } } });
        
        [this.previousSceneKey, failedTargetScene].forEach(key => { if (key && key !== 'N/A' && key !== recoveryTarget && this.gameInstance.scene.isActive(key)) { try { this.gameInstance.scene.stop(key); } catch(e){} } });
        
        setTimeout(() => {
            try {
                if (this.gameInstance.scene.keys[recoveryTarget]) {
                     console.log(`[Recovery] Starting recovery scene: ${recoveryTarget}`); 
                     this.gameInstance.scene.start(recoveryTarget); 
                     this.currentSceneKey = recoveryTarget;
                     setTimeout(() => { if (window.UIController) { window.UIController.setActiveScene(this.getActiveSceneInstance()); window.UIController.enableSceneInput(); } }, 200);
                } else {
                     console.error(`[Recovery] FAILED: Recovery scene ${recoveryTarget} not found.`); 
                     // CRITICAL FIX: Use feature flag routing for final fallback
                     const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
                     const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
                     const finalFallback = useUniversalLanding ? "MainMenuSceneUniversal" :
                                          (useDOMMenus ? "GuestMenuSceneDOM" : "GuestMenuScene");
                     console.log(`[Recovery] Using final fallback: ${finalFallback}`);
                     this.gameInstance.scene.start(finalFallback); 
                     this.currentSceneKey = finalFallback;
                     setTimeout(() => { if (window.UIController) window.UIController.enableSceneInput(); }, 100);
                }
            } catch (recoveryStartError) {
                 console.error("[Recovery] FAILED: Error starting recovery scene:", recoveryStartError); 
                 // CRITICAL FIX: Use feature flag routing for error fallback
                 const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
                 const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
                 const finalFallback = useUniversalLanding ? "MainMenuSceneUniversal" :
                                    (useDOMMenus ? "GuestMenuSceneDOM" : "GuestMenuScene");
                 console.log(`[Recovery] Final fallback due to error: ${finalFallback}`);
                 try { this.gameInstance.scene.start(finalFallback); this.currentSceneKey = finalFallback; } catch(e){}
                 setTimeout(() => { if (window.UIController) window.UIController.enableSceneInput(); }, 100);
            }
        }, this.POST_STOP_DELAY * 5);
    } catch (recoverySetupError) {
        console.error("[Recovery] FAILED: Error during recovery setup:", recoverySetupError); 
        // CRITICAL FIX: Use feature flag routing for final recovery fallback
        const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
        const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
        const finalFallback = useUniversalLanding ? "MainMenuSceneUniversal" :
                             (useDOMMenus ? "GuestMenuSceneDOM" : "GuestMenuScene");
        try { this.gameInstance.scene.start(finalFallback); this.currentSceneKey = finalFallback; } catch(e){}
        setTimeout(() => { if (window.UIController) window.UIController.enableSceneInput(); }, 100);
    }
}

    _processQueue() {
        if (this.transitionQueue.length > 0 && !this.isTransitioning) {
            const nextTransition = this.transitionQueue.shift();
            console.log(`%c[SceneManager] Processing queued transition: -> ${nextTransition.targetSceneKey}`, 'color: #FFA500');
            setTimeout(() => {
                if (!this.isTransitioning) {
                    this.switchToScene(nextTransition.targetSceneKey, nextTransition.data);
                } else {
                     console.warn(`[SceneManager] Cannot start queued transition to ${nextTransition.targetSceneKey}, another transition started unexpectedly. Re-queuing.`);
                     this.transitionQueue.unshift(nextTransition);
                }
            }, 50);
        }
    }

    cleanupAuthenticationDialogs() {
        console.log("[SceneManager] Cleaning up authentication dialogs during transition");
        
        // Remove all authentication overlays
        const authOverlays = document.querySelectorAll('.auth-overlay, #auth-overlay, .auth-dialog');
        authOverlays.forEach(overlay => {
            overlay.style.display = 'none';
            overlay.classList.remove('active', 'show');
        });
        
        // Clean up new authentication scene overlays
        const sceneOverlays = document.querySelectorAll('#signin-scene-overlay, #signup-scene-overlay');
        sceneOverlays.forEach(overlay => {
            if (overlay.parentNode) {
                overlay.parentNode.removeChild(overlay);
                console.log(`[SceneManager] Removed scene overlay: ${overlay.id}`);
            }
        });
        
        // Hide professional authentication dialogs only during actual scene transitions, not when staying in same scene
        if (window.authDialogs && typeof window.authDialogs.closeDialog === 'function') {
            // Only close dialogs if we're actually transitioning away from the current scene
            const currentSceneKey = this.currentSceneKey;
            const targetSceneKey = this._targetSceneKey;
            
            if (currentSceneKey && targetSceneKey && currentSceneKey !== targetSceneKey) {
                console.log(`[SceneManager] Closing auth dialogs for scene transition: ${currentSceneKey} -> ${targetSceneKey}`);
                window.authDialogs.closeDialog();
                
                // CRITICAL: Clean up template overlays during scene transitions
                if (window.templateOverlayManager) {
                    console.log('[SceneManager] Cleaning up template overlays during transition');
                    window.templateOverlayManager.prepareForTransition(targetSceneKey);
                }
            } else {
                console.log(`[SceneManager] Keeping auth dialogs open (no scene change or same scene)`);
            }
        }
        
        // Hide any UIController overlays that might be auth-related
        if (window.UIController) {
            try {
                window.UIController.hideOverlay('auth-overlay');
                window.UIController.hideOverlay('signup-overlay');
                window.UIController.hideOverlay('signin-overlay');
            } catch (error) {
                console.warn("Error hiding auth overlays:", error);
            }
        }
        
        console.log("[SceneManager] Authentication dialogs cleanup completed");
    }

} // End of SceneManager class

const sceneManager = new SceneManager();
export default sceneManager;